import EntityType from '@enums/EntityType';
import updateMetrics from '@pkg/entities/snapshots/derive/shared/updateMetrics';
import getMetrics from './getMetrics';

const getUniqueMetrics = ({
  addActivities,
  addGroups,
  addRoles,
  entity,
  metrics,
}) => {
  let count = 1;

  return {
    ...metrics,
    count,
    activities: addActivities ? metrics.activities : 0,
    groups: addGroups ? metrics.groups : 0,
    roles: addRoles ? metrics.roles : 0,
    spans: 0,
  };
};

const getEntityMetrics = ({
  entity,
  activityMetrics,
  groupMetrics,
  roleMetrics,
}) => {
  if (activityMetrics) {
    return { ...activityMetrics };
  }
  if (roleMetrics) {
    return { ...roleMetrics };
  }
  return { ...groupMetrics };
};

const getUniqueId = ({ entity, activity, role, group, treeItem }) => {
  let id = entity.uuid;

  if (entity.__type === EntityType.ROLE_TYPE) {
    id = `${id}_${role.uuid}`;
    return activity ? `${id}_${activity.uuid}` : id;
  }

  if (entity.__type === EntityType.ACTIVITY_TYPE) {
    id = `${id}_${activity.uuid}`;
  }

  if (activity) {
    return `${id}_${activity.uuid}`;
  }

  if (role) {
    return `${id}_${role.uuid}`;
  }

  if (group) {
    return `${id}_${group.uuid}`;
  }

  return `${id}_${entity.uuid || entity.id}`;
};

export default function buildItemMetrics({
  designId,
  entities,
  entity,
  entityLevelOnly,
  entitySet,
  property,
  treeItem,
}) {
  const activity = entities[EntityType.ACTIVITY];
  const role = entities[EntityType.ROLE];
  const group = entities[EntityType.GROUP];
  const manager =
    entities[EntityType.MANAGER] ?? (role?.is_manager ? role : null);
  const entityKey = getUniqueId({ activity, entity, group, role, treeItem });

  const activityMetrics = getMetrics({ entity: activity });
  const roleMetrics = getMetrics({ entity: role });
  const groupMetrics = getMetrics({ entity: group });
  const entityMetrics = getEntityMetrics({
    entity,
    activityMetrics: activity ? activityMetrics : null,
    roleMetrics: role ? roleMetrics : null,
    groupMetrics: group ? groupMetrics : null,
  });

  if (!treeItem?.[property]?.[designId]) {
    const metrics = {
      ...entityMetrics,
      ids: {
        entities: new Set([entityKey]),
        [EntityType.ACTIVITY]: activity ? new Set([activity.uuid]) : new Set(),
        [EntityType.ROLE]: role ? new Set([role.uuid]) : new Set(),
        [EntityType.GROUP]: group ? new Set([group.uuid]) : new Set(),
        [EntityType.MANAGER]: manager ? new Set([manager.uuid]) : new Set(),
      },
    };

    if (!entityLevelOnly) {
      metrics[EntityType.ACTIVITY] = { ...activityMetrics };
      metrics[EntityType.GROUP] = { ...groupMetrics };
      metrics[EntityType.ROLE] = { ...roleMetrics };
    }
    return metrics;
  }

  const entityIds = treeItem[property][designId].ids;

  const addEntityMetrics = !entityIds.entities.has(entityKey);

  const addActivityMetrics =
    activity && !entityIds[EntityType.ACTIVITY].has(activity.uuid);

  const addGroupMetrics = group && !entityIds[EntityType.GROUP].has(group.uuid);

  const addRoleMetrics = role && !entityIds[EntityType.ROLE].has(role.uuid);

  const addManager =
    manager && !entityIds[EntityType.MANAGER].has(manager.uuid);

  let metrics;

  // Add entity metrics.
  if (addEntityMetrics) {
    metrics = updateMetrics({
      source: treeItem[property][designId],
      target: getUniqueMetrics({
        addCount: addEntityMetrics,
        addActivities: addActivityMetrics,
        addGroups: addGroupMetrics,
        addRoles: addRoleMetrics,
        metrics: entityMetrics,
        entity,
      }),
    });
    treeItem[property][designId].ids.entities.add(entityKey);
  } else {
    metrics = treeItem[property][designId];
  }

  metrics.ids = treeItem[property][designId].ids;

  // Add activity metrics.
  if (addActivityMetrics && !entityLevelOnly) {
    metrics[EntityType.ACTIVITY] = updateMetrics({
      source: treeItem[property][designId][EntityType.ACTIVITY],
      target: getUniqueMetrics({
        addActivities: addActivityMetrics,
        addGroups: addGroupMetrics,
        addRoles: addRoleMetrics,
        metrics: activityMetrics,
      }),
    });
    metrics.ids[EntityType.ACTIVITY].add(activity.uuid);
  } else {
    metrics[EntityType.ACTIVITY] =
      treeItem[property][designId][EntityType.ACTIVITY];
  }

  // Add activity metrics.
  if (addGroupMetrics && !entityLevelOnly) {
    metrics[EntityType.GROUP] = updateMetrics({
      source: treeItem[property][designId][EntityType.GROUP],
      target: getUniqueMetrics({
        addActivities: addActivityMetrics,
        addRoles: addRoleMetrics,
        metrics: groupMetrics,
      }),
    });
    metrics.ids[EntityType.GROUP].add(group.uuid);
  } else {
    metrics[EntityType.GROUP] = treeItem[property][designId][EntityType.GROUP];
  }

  if (addRoleMetrics && !entityLevelOnly) {
    metrics[EntityType.ROLE] = updateMetrics({
      source: treeItem[property][designId][EntityType.ROLE],
      target: getUniqueMetrics({
        metrics: roleMetrics,
        addActivities: addActivityMetrics,
        addRoles: addRoleMetrics,
        addGroups: addGroupMetrics,
      }),
    });
    metrics.ids[EntityType.ROLE].add(role.uuid);
  } else {
    metrics[EntityType.ROLE] = treeItem[property][designId][EntityType.ROLE];
  }

  if (addManager) {
    metrics.ids[EntityType.MANAGER].add(manager.uuid);
  }

  return metrics;
}
